import useSiteNavigation from '../../hooks/useSiteNavigation';
/* eslint-disable prefer-const */
import React, { useState, useEffect, useRef } from 'react';
//@ts-ignore
import * as styles from './navigation.module.css';
//@ts-ignore
import * as closeButtonStyles from '../Global/CloseButton/CloseButton.module.css';
import { CloseIcon } from '../Global/CloseButton';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { MediaQueries } from '../Global/GlobalMediaQueries';
import {
    renderNavIconCard,
    renderStandardLink,
    renderRichTextCard,
    renderContentCard,
} from './secondaryNavElements';

import { SiteSearch } from './SiteSearch';
import {
    NavigationProps,
    DesktopNavElements,
    MobileNavElements,
    navIconCard,
    navContentCard,
    navRichTextCard,
    navStandardLink,
    navColorConfig,
    NavGroup,
    LongLogo,
    ShortLogo,
} from './index';
import {
    getColor,
    getLocalIcon,
    getMediaData,
    getMultiChoice,
    localIconUrl,
    optimizeKontentImageUrl,
} from '../Global/DataUtils/index';
import SvgArrowLgRight from '../Global/ArrowLgButton/SvgArrowLgRight';
import SvgArrowLgLeft from '../Global/ArrowLgButton/SvgArrowLgLeft';
import CountrySelectOverlay from '../CountrySelectOverlay';
import LazyImage from '../Global/LazyImage';
import { getLangPrefix } from '../../global/countries';
import { Link } from 'gatsby';
import { withTrailingSlash } from '../../utils/trailingSlash';
import { useShopUsHref } from '../../hooks/useShopUsHref';
import { KontentIcon } from '../../../types/KontentProps';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';

interface Props extends NavigationProps {
    preferredLanguage?: string;
}

const Navigation: React.FC<Props> = ({
    search_icon,
    shop_cta_icon,
    shop_cta_location,
    shop_mobile_label,
    account_cta_icon,
    account_cta_location,
    account_mobile_label,
    main_nav_icon,
    color_configuration,
    long_logo,
    short_logo,
    groups,
    country_select_overlay_configuration,
    preferredLanguage,
    kontentItemId,
    ...props
}) => {
    const shopHref = useShopUsHref(shop_cta_location?.value);
    const navGroups = groups?.value || [];
    const [activePanel, setActivePanel] = useState(navGroups.length);
    const [navOpen, setNavOpen] = useState(false);
    const [navScroll, setNavScroll] = useState(false);
    const navFlag = true;
    const [countrySelectOpen, setCountrySelectOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const mobileMenuId = React.useId();
    const breakpoints = useBreakpoint();
    const bottomNavRef = useRef(null);
    const navRef = useRef(null);
    const noResultHeadline =
        props?.no_search_results_headline?.value ||
        'Let’s help you find what you’re looking for.';
    const noResultCopy =
        props?.no_search_results_body_copy?.value ||
        'It looks like we couldn’t find any matches for your search. Try adjusting your search terms, or check out some helpful links below.';
    const prevActivePanelRef = useRef(activePanel);
    const homeHref = getLangPrefix(preferredLanguage) || '/';
    const { location: pageLocation } = React.useContext(PageMetadataContext);
    const pathname =
        typeof pageLocation === 'object' ? pageLocation.pathname : pageLocation;
    useEffect(() => {
        setNavOpen(false);
        setCountrySelectOpen(false);
        setSearchTerm('');
    }, [pathname]);
    const useCountryCode = homeHref.split('/')[1] !== '' ? true : false;
    const showShopLabel =
        getMultiChoice(props?.show_shop_mobile_label_on_desktop) === 'yes' ||
        false;
    const showaccountLabel =
        getMultiChoice(props?.show_account_mobile_label_on_desktop) === 'yes' ||
        false;

    useEffect(() => {
        if (activePanel < prevActivePanel || !navOpen) {
            setSearchTerm('');
        }

        prevActivePanelRef.current = activePanel;
    }, [activePanel, navOpen]);

    let prevActivePanel = prevActivePanelRef.current;

    const authorStyles = setAuthorStyles(color_configuration);

    const [navigationStyles, setNavigationStyles] = useState(
        authorStyles.inline
    );

    // use intersection observer to set nav scroll
    const handleBottomNavIntersection = (
        entries: IntersectionObserverEntry[]
    ) => {
        if (navOpen) return;
        const [entry] = entries;
        setNavScroll(!entry.isIntersecting);
    };

    const bottomNavIntersectionOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            handleBottomNavIntersection,
            bottomNavIntersectionOptions
        );

        if (bottomNavRef === null) return;

        if (bottomNavRef && bottomNavRef?.current) {
            //@ts-ignore
            observer.observe(bottomNavRef.current);
        }

        return () => {
            if (bottomNavRef && bottomNavRef?.current) {
                //@ts-ignore
                observer.unobserve(bottomNavRef.current);
            }
        };
    }, [navOpen]);

    // change inlineStyle if user opens navigation before scrolling
    useEffect(() => {
        const getNavigationStyles = () => {
            if (navScroll) return;

            return authorStyles.inline;
        };

        let newNavigationStyles = getNavigationStyles();
        if (newNavigationStyles) {
            setNavigationStyles(newNavigationStyles);
        }
    }, [navOpen]);

    // handles click outside
    useEffect(() => {
        if (!navRef?.current) {
            return;
        }

        const handleClickOutside = (e: MouseEvent) => {
            if (!navOpen || !navRef?.current) return;

            if (
                navOpen &&
                !navRef?.current?.contains(e.target) &&
                !countrySelectOpen
            ) {
                toggleActive(activePanel);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [navRef, navOpen]);

    // Update debouncedTerm after 2 seconds
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedTerm(searchTerm);
        }, 2000);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // Update query params when debouncedTerm changes
    useEffect(() => {
        if (debouncedTerm) {
            const url = new URL(window.location);
            url.searchParams.set('search', debouncedTerm);
            window.history.pushState({}, '', url);
        }
    }, [debouncedTerm]);

    const iconColor = navScroll || navOpen ? '#505251' : authorStyles.color;
    const accentColor =
        navScroll || navOpen ? '#f06623' : authorStyles.accentColor;
    const findmysaunagray = navScroll ? styles.greySaunaScrolled : '';
    let showLogoImage = setLogoImage(
        long_logo,
        short_logo,
        breakpoints,
        navScroll
    );

    // close navigation if window size changes
    useEffect(() => {
        setNavOpen(false);
        setActivePanel(navGroups.length);
    }, [breakpoints]);

    const toggleActive = (idx: number) => {
        if (navOpen) {
            switch (idx) {
                case navGroups.length:
                    setNavOpen(!navOpen);
                    return;
                case activePanel:
                    setNavOpen(false);

                    setActivePanel(navGroups.length);
                    return;
            }
        }
        setActivePanel(idx);
        setNavOpen(true);
    };

    const handleToggleNavbar = () => {
        toggleActive(navGroups.length);
    };

    const handleMobileMenuKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
        if (
            event.key === 'Enter' ||
            event.key === ' ' ||
            event.key === 'Spacebar' ||
            event.key === 'Space' ||
            event.code === 'Space'
        ) {
            event.preventDefault();
            handleToggleNavbar();
        }
    };

    const searchIcon = getLocalIcon(search_icon);
    const shopIcon = getLocalIcon(shop_cta_icon);
    const accountIcon = getLocalIcon(account_cta_icon);
    const mainNavIcon = getLocalIcon(main_nav_icon);

    // contain: local WebP icons are 2x rasters and must scale into the box
    // the way the viewBox-only SVGs do.
    const iconStyles = {
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
    };

    const shopIconStyles = {
        backgroundImage: `url(${shopIcon.url})`,
        maskImage: `url(${shopIcon.url})`,
        WebkitMaskImage: `url(${shopIcon.url})`,
        ...iconStyles,
    };

    const searchIconStyles = {
        backgroundImage: `url(${searchIcon.url})`,
        maskImage: `url(${searchIcon.url})`,
        WebkitMaskImage: `url(${searchIcon.url})`,
        cursor: 'pointer',
        ...iconStyles,
    };

    const accountIconStyles = {
        backgroundImage: `url(${accountIcon.url})`,
        maskImage: `url(${accountIcon.url})`,
        WebkitMaskImage: `url(${accountIcon.url})`,
        ...iconStyles,
    };

    const mainNavIconStyles = {
        maskImage: `url(${mainNavIcon.url})`,
        WebkitMaskImage: `url(${mainNavIcon.url})`,
        ...iconStyles,
    };

    const renderMobileNavFooter = () => {
        const year = new Date().getFullYear();

        return (
            <div className={styles.mobileNavFooter}>
                {shop_cta_location?.value && (
                    <div className={styles.mobileFooterIconLink}>
                        <a
                            id={`footer__${
                                shop_mobile_label?.value?.replace(/ /g, '-') ||
                                'Shop'
                            }`}
                            href={withTrailingSlash(shopHref)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span
                                className={styles.mobileFooterIcon}
                                style={shopIconStyles}
                            ></span>{' '}
                            <span data-kontent-element-codename="shop_mobile_label">
                                {shop_mobile_label?.value || 'Shop'}
                            </span>
                        </a>
                    </div>
                )}
                {account_cta_location?.value && (
                    <div className={styles.mobileFooterIconLink}>
                        <a
                            id={`footer__${
                                account_mobile_label?.value?.replace(
                                    / /g,
                                    '-'
                                ) || 'Shop'
                            }`}
                            href={withTrailingSlash(account_cta_location.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span
                                className={styles.mobileFooterIcon}
                                style={accountIconStyles}
                            ></span>{' '}
                            <span data-kontent-element-codename="account_mobile_label">
                                {account_mobile_label?.value || 'Account'}
                            </span>
                        </a>
                    </div>
                )}
                {country_select_overlay_configuration?.value?.[0]?.elements && (
                    <div>
                        <div className={styles.countrySelectButton}>
                            <CountrySelectOverlay
                                {...country_select_overlay_configuration
                                    .value[0].elements}
                            />
                        </div>
                    </div>
                )}
                <div data-kontent-element-codename="mobile_copyright_copy">
                    {props.mobile_copyright_copy?.value} {year}
                </div>
            </div>
        );
    };

    const handleSearchUserIntent = () => {
        setActivePanel(navGroups.length + 1);
    };

    const renderDesktopGroupPanel = (navPanel: NavGroup, panelIdx: number) => {
        const desktopElements = navPanel?.elements?.desktop_elements?.value;
        if (!desktopElements?.length) return null;

        const panelName = navPanel?.elements?.category_name;
        const isActive = activePanel === panelIdx;

        return (
            <div
                key={panelIdx}
                id={`navigation-panel-${panelIdx}`}
                className={styles.group}
                hidden={isActive ? undefined : true}
                aria-hidden={isActive ? undefined : true}
            >
                {desktopElements?.map(
                    (element: DesktopNavElements, idx: number) => {
                        if (!element?.system?.type) return null;

                        const cardAuthorStyles = {
                            color: iconColor,
                            accentColor: accentColor,
                        };

                        switch (element.system.type) {
                            case 'nav___icon_card':
                                return (
                                    <React.Fragment key={idx}>
                                        {renderNavIconCard(
                                            element as navIconCard,
                                            cardAuthorStyles,
                                            panelName
                                        )}
                                    </React.Fragment>
                                );
                            case 'nav___content_card_with_image':
                                return (
                                    <React.Fragment key={idx}>
                                        {renderContentCard(
                                            element as navContentCard,
                                            cardAuthorStyles,
                                            panelName
                                        )}
                                    </React.Fragment>
                                );
                            case 'nav___rich_text_card': {
                                const modifiedPanelName = panelName?.value
                                    ? {
                                          value:
                                              'navigation__' + panelName.value,
                                      }
                                    : undefined;
                                return (
                                    <React.Fragment key={idx}>
                                        {renderRichTextCard(
                                            element as navRichTextCard,
                                            modifiedPanelName
                                        )}
                                    </React.Fragment>
                                );
                            }
                            default:
                                return null;
                        }
                    }
                )}
            </div>
        );
    };

    const renderDesktopNavPanel = () => {
        return (
            <>
                {navGroups.map((navPanel, panelIdx) => {
                    if (!navPanel?.elements) return null;
                    return renderDesktopGroupPanel(navPanel, panelIdx);
                })}
            </>
        );
    };

    const renderMobilePrimaryPanel = () => {
        return (
            <div
                hidden={activePanel === navGroups.length ? undefined : true}
                aria-hidden={
                    activePanel === navGroups.length ? undefined : true
                }
            >
                <ul className={styles.mobilePrimaryNav}>
                    {navGroups.map((group, idx) => {
                        if (
                            !group?.elements ||
                            group.elements.iscta?.value?.[0]?.name === 'Yes'
                        ) {
                            return null;
                        }
                        const groupElements = group.elements;
                        const mobileDisplayName =
                            groupElements.display_name?.value;
                        const mobileCtaSlug =
                            groupElements.cta_location?.value?.[0]?.elements
                                ?.slug?.url;
                        const mobileWindowAction = getMultiChoice(
                            groupElements.cta_window_action
                        );

                        return (
                            <li key={idx}>
                                {mobileDisplayName && mobileCtaSlug ? (
                                    <a
                                        id={`navigation__${mobileDisplayName.replace(
                                            / /g,
                                            '-'
                                        )}-mobile`}
                                        className={styles.directLinkMobile}
                                        href={withTrailingSlash(
                                            `${useCountryCode ? homeHref : ''}${
                                                groupElements.anchor?.value
                                                    ? `${mobileCtaSlug}#${groupElements.anchor.value}`
                                                    : `${mobileCtaSlug}/`
                                            }`
                                        )}
                                        target={
                                            mobileWindowAction === 'same_window'
                                                ? undefined
                                                : '_blank'
                                        }
                                        rel={
                                            mobileWindowAction === 'same_window'
                                                ? undefined
                                                : 'noopener noreferrer'
                                        }
                                    >
                                        <span
                                            className={styles.mobileButtonLink}
                                        >
                                            <span
                                                className={styles.buttonCopy}
                                                data-kontent-element-codename="display_name"
                                            >
                                                {mobileDisplayName}
                                            </span>
                                        </span>
                                    </a>
                                ) : (
                                    <button
                                        onClick={() => setActivePanel(idx)}
                                        aria-expanded={
                                            navOpen && activePanel === idx
                                        }
                                        aria-controls={
                                            groupElements.mobile_elements?.value
                                                ?.length
                                                ? `navigation-panel-${idx}-mobile`
                                                : undefined
                                        }
                                        className={styles.mobileButtonLink}
                                        id={`navigation__${groupElements.category_name?.value?.replace(
                                            / /g,
                                            '-'
                                        )}-mobile`}
                                    >
                                        <span
                                            className={styles.buttonCopy}
                                            data-kontent-element-codename="category_name"
                                        >
                                            {groupElements.category_name?.value}
                                        </span>
                                        <div className={styles.arrowContainer}>
                                            <div className={styles.forward}>
                                                <SvgArrowLgRight
                                                    strokeColor={iconColor}
                                                />
                                            </div>
                                        </div>
                                    </button>
                                )}
                            </li>
                        );
                    })}
                    <li className={styles.mobileSearch}>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSearchUserIntent();
                            }}
                            className={styles.searchForm}
                        >
                            <div
                                className={styles.navIcon}
                                style={searchIconStyles}
                            >
                                <span className="sr-only">Search</span>
                            </div>
                            <input
                                aria-label="Search"
                                placeholder={props.keyword_hint_text?.value}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </form>
                    </li>
                </ul>
            </div>
        );
    };

    const renderMobileSecondaryPanel = (
        navPanel: NavGroup,
        panelIdx: number
    ) => {
        const mobileElements = navPanel?.elements?.mobile_elements?.value;
        if (!mobileElements?.length) return null;

        const panelName = navPanel?.elements?.category_name;
        const mobilePanelName = {
            value: `${panelName?.value || 'nav'}-mobile`,
        };
        const isActive = activePanel === panelIdx;

        return (
            <div
                key={panelIdx}
                id={`navigation-panel-${panelIdx}-mobile`}
                hidden={isActive ? undefined : true}
                aria-hidden={isActive ? undefined : true}
            >
                <div className={styles.mobileSecondaryNav}>
                    <button
                        onClick={() => setActivePanel(navGroups.length)}
                        className={
                            styles.mobileButtonLink +
                            ' ' +
                            styles.mobileBackLink
                        }
                    >
                        <div className={styles.arrowContainer}>
                            <div className={styles.back}>
                                <SvgArrowLgLeft strokeColor={iconColor} />
                            </div>
                        </div>

                        <span
                            className={styles.buttonCopy}
                            data-kontent-element-codename="category_name"
                        >
                            {panelName?.value}
                        </span>
                    </button>
                </div>

                <div className={styles.group}>
                    {mobileElements?.map(
                        (element: MobileNavElements, idx: number) => {
                            if (!element?.system?.type) return null;

                            switch (element.system.type) {
                                case 'nav___icon_card':
                                    return (
                                        <React.Fragment key={idx}>
                                            {renderNavIconCard(
                                                element as navIconCard,
                                                authorStyles,
                                                mobilePanelName
                                            )}
                                        </React.Fragment>
                                    );
                                case 'nav___standard_link':
                                    return (
                                        <React.Fragment key={idx}>
                                            {renderStandardLink(
                                                element as navStandardLink,
                                                mobilePanelName
                                            )}
                                        </React.Fragment>
                                    );
                                default:
                                    return null;
                            }
                        }
                    )}
                </div>
            </div>
        );
    };

    const renderMobileNavPanel = () => {
        return (
            <>
                {renderMobilePrimaryPanel()}
                {navGroups.map((navPanel, panelIdx) => {
                    if (!navPanel?.elements) return null;
                    return renderMobileSecondaryPanel(navPanel, panelIdx);
                })}
            </>
        );
    };

    const renderSearchPanel = () => {
        if (activePanel !== navGroups.length + 1) return null;

        if (breakpoints.xl) {
            return (
                <SiteSearch
                    toggleNavbar={handleToggleNavbar}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    goBackAction={() => setActivePanel(navGroups.length)}
                    authorStyles={authorStyles}
                    preferredLanguage={preferredLanguage || 'default'}
                    ariaLabel={props.aria_label?.value}
                    keywordHintText={props.keyword_hint_text?.value}
                    searchPanelTitle={props.search_panel_title?.value}
                    desktopSearchCtaLabel={
                        props.desktop_search_cta_label?.value
                    }
                    noResultHeadline={noResultHeadline}
                    noResultCopy={noResultCopy}
                />
            );
        }

        return (
            <SiteSearch
                toggleNavbar={handleToggleNavbar}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                goBackAction={() => setActivePanel(navGroups.length)}
                authorStyles={authorStyles}
                backLabel="back"
                preferredLanguage={preferredLanguage || 'default'}
                ariaLabel={props.aria_label?.value}
                keywordHintText={props.keyword_hint_text?.value}
                searchPanelTitle={props.search_panel_title?.value}
                desktopSearchCtaLabel={props.desktop_search_cta_label?.value}
                noResultHeadline={noResultHeadline}
                noResultCopy={noResultCopy}
            />
        );
    };

    const renderExpandNav = () => {
        return (
            <div
                id={mobileMenuId}
                className={`${styles.expandNav} ${
                    navOpen ? '' : styles.expandNavClosed
                }`}
                aria-hidden={navOpen ? undefined : true}
            >
                <div className={styles.expandNavInner}>
                    {renderSearchPanel()}
                    <div className={styles.desktopExpandNav}>
                        {renderDesktopNavPanel()}
                    </div>
                    <div className={styles.mobileExpandNav}>
                        {renderMobileNavPanel()}
                        {renderMobileNavFooter()}
                    </div>
                </div>
            </div>
        );
    };

    const renderTopMenuIcons = () => {
        const searchIconId = getNavIconDomId(search_icon, 'Search');
        const shopIconId = getNavIconDomId(shop_cta_icon, 'Shop');
        const accountIconId = getNavIconDomId(account_cta_icon, 'Account');

        return (
            <ul className={styles.navIcons}>
                <li className={styles.desktopNavIcon}>
                    <button
                        type="button"
                        id={`navigation__${searchIconId}`}
                        onClick={() => toggleActive(navGroups.length + 1)}
                        className={styles.navIcon}
                        style={searchIconStyles}
                    >
                        <span className="sr-only">Search</span>
                    </button>
                </li>
                {shop_cta_location?.value && (
                    <li
                        className={`${styles.desktopNavIcon} ${
                            showShopLabel ? styles.iconLabel : ''
                        }`}
                    >
                        <a
                            id={`navigation__${shopIconId}`}
                            href={withTrailingSlash(shopHref)}
                            className={styles.navIcon}
                            style={shopIconStyles}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className="sr-only">
                                {shop_mobile_label?.value || 'Shop'}
                            </span>
                        </a>
                        {showShopLabel && (
                            <a
                                href={withTrailingSlash(shopHref)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span>
                                    {shop_mobile_label?.value || 'Shop'}
                                </span>
                            </a>
                        )}
                    </li>
                )}
                {account_cta_location?.value && (
                    <li
                        className={`${styles.desktopNavIcon} ${
                            showaccountLabel ? styles.iconLabel : ''
                        }`}
                    >
                        <a
                            id={`navigation__${accountIconId}`}
                            href={withTrailingSlash(account_cta_location.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.navIcon}
                            style={accountIconStyles}
                        >
                            <span className="sr-only">
                                {account_mobile_label?.value || 'Account'}
                            </span>
                        </a>
                        {showaccountLabel && (
                            <a
                                href={withTrailingSlash(
                                    account_cta_location.value
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span>
                                    {account_mobile_label?.value || 'Account'}
                                </span>
                            </a>
                        )}
                    </li>
                )}

                {country_select_overlay_configuration?.value?.[0]?.elements && (
                    <li className={styles.flagIcon}>
                        <CountrySelectOverlay
                            navFlag={navFlag}
                            {...country_select_overlay_configuration.value[0]
                                .elements}
                        />
                    </li>
                )}
                <li className={styles.mobileMenuToggle}>
                    <button
                        type="button"
                        id="navigation__Open-Menu"
                        onClick={handleToggleNavbar}
                        onKeyDown={handleMobileMenuKeyDown}
                        aria-controls={mobileMenuId}
                        aria-expanded={navOpen}
                        aria-label={navOpen ? 'Close Menu' : 'Open Menu'}
                        className={
                            navOpen
                                ? `${styles.closeButton} ${closeButtonStyles.closeButton}`
                                : styles.navIcon
                        }
                        style={navOpen ? undefined : mainNavIconStyles}
                    >
                        {navOpen && <CloseIcon />}
                    </button>
                </li>
            </ul>
        );
    };

    const handleSiteNavigation = useSiteNavigation(() => setNavOpen(false));

    return (
        <div data-kontent-item-id={kontentItemId}>
            <nav
                aria-label="Main navigation"
                onClick={handleSiteNavigation}
                className={`${styles.navigation} ${
                    navScroll || navOpen ? styles.navDefault : ''
                } ${
                    navOpen ? `${styles.navOpen} ${styles.higherZindex}` : ''
                } ${breakpoints.xl ? styles.hideOverflow : ''}`}
                style={navScroll || navOpen ? undefined : navigationStyles}
                ref={navRef}
            >
                {authorStyles.color && (
                    <style>
                        {`
                        .${styles.navIcon} { 
                        background-color: ${iconColor} !important;
                        }
                        .${styles.closeButton} path {
                            stroke: ${iconColor} !important;
                        }
                        .${styles.closeButton}:hover path {
                            stroke: ${accentColor} !important;
                        }
                        .${styles.navIcon}:hover {
                            background-color: ${accentColor} !important;
                        }
                        .${styles.categoryName}:hover, .${styles.mobileButtonLink}:hover, .${styles.standardLink}:hover {
                            color: ${accentColor}
                        }
                        .${styles.mobileButtonLink}:hover .${styles.arrowContainer} path { stroke: ${accentColor} !important}

                        .${styles.contentCardLink}:hover div.${styles.cta} { 
                            border-color: ${accentColor} !important; 
                            color: ${iconColor} !important;
                            background-color: ${accentColor} !important;}
                        `}
                    </style>
                )}
                <div className={styles.navContainer}>
                    <div className={styles.collapseNav}>
                        <a
                            href={withTrailingSlash(homeHref)}
                            target="_self"
                            id="navigation__Sunlighten-logo"
                        >
                            <LazyImage
                                src={showLogoImage ?? ''}
                                className={styles.logoImage}
                                alt="Sunlighten"
                                width={182}
                                height={87}
                                priority
                            />
                        </a>
                        <ul>
                            {navGroups.map(
                                (group: NavGroup | null, idx: number) => {
                                    if (!group?.elements) {
                                        return null;
                                    }
                                    const groupElements = group.elements;
                                    let uniqueID =
                                        groupElements.category_name?.value ||
                                        groupElements.display_name?.value;
                                    uniqueID = uniqueID?.replace(/ /g, '-');
                                    const isCta =
                                        groupElements.iscta?.value?.[0]
                                            ?.name === 'Yes';
                                    const displayName =
                                        groupElements.display_name?.value;
                                    const ctaSlug =
                                        groupElements.cta_location?.value?.[0]
                                            ?.elements?.slug?.url;
                                    const pageSlug =
                                        groupElements.element?.value?.[0]
                                            ?.elements?.slug?.url;
                                    const windowAction = getMultiChoice(
                                        groupElements.cta_window_action
                                    );

                                    return (
                                        <li
                                            key={idx}
                                            className={`${
                                                isCta
                                                    ? `${findmysaunagray} ${styles.findMySaunaCta}`
                                                    : styles.categoryName
                                            }`}
                                            style={{
                                                ...(isCta
                                                    ? {
                                                          border: `2px solid ${iconColor}`,
                                                      }
                                                    : {}),
                                                ...(idx === activePanel
                                                    ? {
                                                          color: authorStyles.accentColor,
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            {displayName && pageSlug ? (
                                                <Link
                                                    id={`navigation__${uniqueID}`}
                                                    to={withTrailingSlash(
                                                        `${
                                                            useCountryCode
                                                                ? homeHref
                                                                : ''
                                                        }${pageSlug}`
                                                    )}
                                                    data-kontent-element-codename="display_name"
                                                >
                                                    {displayName}
                                                </Link>
                                            ) : displayName && ctaSlug ? (
                                                <a
                                                    id={`navigation__${uniqueID}`}
                                                    href={withTrailingSlash(
                                                        `${
                                                            useCountryCode
                                                                ? homeHref
                                                                : ''
                                                        }${
                                                            groupElements.anchor
                                                                ?.value
                                                                ? `${ctaSlug}#${groupElements.anchor.value}`
                                                                : `${ctaSlug}/`
                                                        }`
                                                    )}
                                                    target={
                                                        windowAction ===
                                                        'same_window'
                                                            ? undefined
                                                            : '_blank'
                                                    }
                                                    rel={
                                                        windowAction ===
                                                        'same_window'
                                                            ? undefined
                                                            : 'noopener noreferrer'
                                                    }
                                                    data-kontent-element-codename="display_name"
                                                >
                                                    {displayName}
                                                </a>
                                            ) : (
                                                <button
                                                    type="button"
                                                    id={`navigation__${uniqueID}`}
                                                    className={
                                                        styles.categoryName
                                                    }
                                                    onClick={() =>
                                                        toggleActive(idx)
                                                    }
                                                    aria-expanded={
                                                        navOpen &&
                                                        activePanel === idx
                                                    }
                                                    aria-controls={
                                                        groupElements
                                                            .desktop_elements
                                                            ?.value?.length
                                                            ? `navigation-panel-${idx}`
                                                            : undefined
                                                    }
                                                >
                                                    {
                                                        groupElements
                                                            .category_name
                                                            ?.value
                                                    }
                                                </button>
                                            )}
                                        </li>
                                    );
                                }
                            )}
                            <li>{renderTopMenuIcons()}</li>
                        </ul>
                    </div>
                    {renderExpandNav()}
                </div>
            </nav>
            <div
                ref={bottomNavRef}
                style={{ position: 'absolute', top: '30%' }}
            ></div>
            {navOpen && (
                <div
                    onClick={() => {
                        setNavOpen(!navOpen);
                    }}
                    className={styles.modalOverlay}
                ></div>
            )}
        </div>
    );
};

export default Navigation;

function setLogoImage(
    long_logo: LongLogo,
    short_logo: ShortLogo,
    breakpoints: MediaQueries,
    navScroll: boolean
) {
    if (
        (!breakpoints.xl || navScroll) &&
        short_logo?.value?.[0]?.elements?.file?.value?.[0]?.url
    ) {
        const shortLogo = getMediaData(short_logo);
        return shortLogo?.url
            ? optimizeKontentImageUrl(localIconUrl(shortLogo.url), {
                  w: 400,
                  fm: 'webp',
                  q: 95,
              })
            : shortLogo?.url;
    }

    const longLogo = getMediaData(long_logo);
    return longLogo?.url
        ? optimizeKontentImageUrl(localIconUrl(longLogo.url), {
              w: 400,
              fm: 'webp',
              q: 95,
          })
        : longLogo?.url;
}

function getNavIconDomId(icon: KontentIcon | undefined, fallback: string) {
    const name = icon?.value?.[0]?.elements?.icon_svg_file?.value?.[0]?.name;
    if (!name) return fallback;
    return name.replace(/ /g, '-').split('.')[0];
}

function setAuthorStyles(color_configuration: navColorConfig) {
    const colorConfig = color_configuration?.value?.[0]?.elements;

    if (!colorConfig) {
        return {
            backgroundColor: '#ffffff',
            color: '#505251',
            accentColor: '#f06623',
            inline: {
                backgroundColor: '#ffffff',
                color: '#505251',
            },
        };
    }

    const backgroundColor = getColor(colorConfig.background_color) || '#ffffff';
    const backgroundOpacity = colorConfig?.background_opacity?.value / 100 || 1;

    const fontColor = getColor(colorConfig.font_color) || '#505251';

    const accentColor =
        backgroundColor === '#f06623' || fontColor === '#f06623'
            ? '#505251'
            : '#f06623';

    const backgroundRgba = hexToRgba(backgroundColor, backgroundOpacity);
    const backgroundStyle =
        backgroundOpacity === 1
            ? { background: backgroundColor }
            : {
                  backgroundImage: `linear-gradient(rgba(${backgroundRgba}), rgba(100, 100, 100, 0))`,
              };

    return {
        backgroundColor: backgroundColor,
        color: fontColor,
        accentColor: accentColor,
        inline: {
            color: fontColor,
            ...backgroundStyle,
        },
    };
}

function hexToRgba(hex: string, opacity: number) {
    hex = hex.replace(/#/g, '');
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map(function (hex) {
                return hex + hex;
            })
            .join('');
    }
    let result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})[\da-z]{0,0}$/i.exec(
        hex
    );
    if (!result) {
        return null;
    }
    let red = parseInt(result[1], 16);
    let green = parseInt(result[2], 16);
    let blue = parseInt(result[3], 16);

    return [red, green, blue, opacity].join(', ');
}
